import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { expertDashboardApi } from '../../../lib/http';

interface InlineClientFormProps {
  onCancel: () => void;
  onSuccess: (client: { id: string; name: string; phone: string; email: string }) => void;
  prefilledName?: string;
}

interface FormData {
  display_name: string;
  custom_email: string;
  custom_phone: string;
}

interface FormErrors {
  display_name?: string;
  custom_email?: string;
  custom_phone?: string;
  contact?: string;
}

const InlineClientForm: React.FC<InlineClientFormProps> = ({
  onCancel,
  onSuccess,
  prefilledName = '',
}) => {
  const [formData, setFormData] = useState<FormData>({
    display_name: prefilledName,
    custom_email: '',
    custom_phone: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus on name field when form appears
    nameInputRef.current?.focus();
  }, []);

  const validateEmail = (email: string): boolean => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true;
    const phoneRegex = /^\+?[\d\s\-()]+$/;
    return phoneRegex.test(phone);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Name is required';
    }

    if (!formData.custom_email.trim() && !formData.custom_phone.trim()) {
      newErrors.contact = 'Provide at least email or phone number';
    }

    if (formData.custom_email && !validateEmail(formData.custom_email)) {
      newErrors.custom_email = 'Invalid email format';
    }

    if (formData.custom_phone && !validatePhone(formData.custom_phone)) {
      newErrors.custom_phone = 'Invalid phone format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await expertDashboardApi.expertClients.create({
        display_name: formData.display_name,
        custom_email: formData.custom_email || undefined,
        custom_phone: formData.custom_phone || undefined,
      });

      const expertClient = response.data.expert_client;
      toast.success('Client added successfully');

      onSuccess({
        id: expertClient.id,
        name: expertClient.display_name,
        phone: expertClient.custom_phone || expertClient.client?.phone || '',
        email: expertClient.custom_email || expertClient.client?.email || '',
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.errors?.join(', ') ||
        error.response?.data?.error ||
        'Failed to add client. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(e as unknown as React.FormEvent);
  };

  return (
    <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-xl animate-slideDown">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Add New Client</h3>
      </div>

      <div className="space-y-3">
        {/* Name */}
        <div>
          <label htmlFor="inline-client-name" className="block text-xs font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            ref={nameInputRef}
            type="text"
            id="inline-client-name"
            value={formData.display_name}
            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm ${
              errors.display_name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="John Smith"
          />
          {errors.display_name && (
            <p className="mt-1 text-xs text-red-600">{errors.display_name}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="inline-client-email" className="block text-xs font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            id="inline-client-email"
            value={formData.custom_email}
            onChange={(e) => setFormData({ ...formData, custom_email: e.target.value })}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm ${
              errors.custom_email ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="john@example.com"
          />
          {errors.custom_email && (
            <p className="mt-1 text-xs text-red-600">{errors.custom_email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="inline-client-phone" className="block text-xs font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            type="tel"
            id="inline-client-phone"
            value={formData.custom_phone}
            onChange={(e) => setFormData({ ...formData, custom_phone: e.target.value })}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm ${
              errors.custom_phone ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="+1234567890"
          />
          {errors.custom_phone && (
            <p className="mt-1 text-xs text-red-600">{errors.custom_phone}</p>
          )}
        </div>

        {errors.contact && (
          <p className="text-xs text-red-600">{errors.contact}</p>
        )}

        {/* Buttons */}
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleButtonClick}
            disabled={isSubmitting}
            className="flex-1 px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Adding...' : 'Add Client'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InlineClientForm;
